import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Can } from '@/shared/ui/Can'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useDebounce } from '@/shared/hooks'
import { cn } from '@/shared/utils'
import {
  Search, Calendar, X, Plus, Trash2, Edit2,
  ChevronLeft, ChevronRight, Image as ImageIcon,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' }).replace(/\//g, '-')
  } catch { return v }
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onChange() }}
      className={cn('relative inline-flex w-9 h-5 rounded-full transition-colors flex-shrink-0',
        checked ? 'bg-brand-500' : 'bg-gray-200')}>
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-4' : 'translate-x-0')}/>
    </button>
  )
}

// ── Upload Banner Modal ───────────────────────────────────────────────────────
function UploadBannerModal({
  open, onClose, onSuccess, editBanner,
}: {
  open: boolean; onClose: () => void; onSuccess: () => void; editBanner?: any
}) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [name,        setName]        = useState(editBanner?.name ?? '')
  const [showUntil,   setShowUntil]   = useState(editBanner?.show_until?.slice(0,10) ?? '')
  const [published,   setPublished]   = useState<boolean>(!!editBanner?.published)
  const [imageFile,   setImageFile]   = useState<File | null>(null)
  const [imagePreview,setImagePreview] = useState<string | null>(
    editBanner?.pictures?.[0]?.picture?.virtual_path ?? null
  )
  const [loading, setLoading]   = useState(false)
  const [error,   setError]     = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const handleSave = async () => {
    if (!name.trim()) { setError('Banner Name is required'); return }
    // Editing an existing banner needs an explicit confirmation; creating doesn't.
    if (editBanner?.id && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setError(''); setLoading(true)
    try {
      const fd = new FormData()
      if (editBanner?.id) fd.append('slider_id', String(editBanner.id))
      fd.append('name', name)
      fd.append('published', published ? '1' : '0')
      if (showUntil) fd.append('show_until', showUntil)
      if (imageFile) fd.append('image', imageFile)

      const url = editBanner?.id ? endpoints.slider.update : endpoints.slider.create
      await restClient.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      onSuccess()
      onClose()
    } catch (e: any) {
      const msg = e?.errors ? Object.values(e.errors).flat().join(', ') : e?.message ?? 'Failed to save'
      setError(msg)
    } finally { setLoading(false) }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Upload Banner</h2>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
            <X size={14} className="text-gray-500"/>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">

          {/* Image upload area */}
          <div
            onClick={() => fileRef.current?.click()}
            className="w-full h-40 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors overflow-hidden bg-gray-50">
            {imagePreview ? (
              <img src={imagePreview} alt="preview" className="w-full h-full object-cover"/>
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-400">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <ImageIcon size={20} className="text-gray-400"/>
                </div>
                <span className="text-xs font-semibold">Upload Banner</span>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange}/>

          {/* Banner Name */}
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1.5">
              Banner Name <span className="text-red-400">*</span>
            </label>
            <input value={name} onChange={e=>setName(e.target.value)}
              placeholder="Enter Banner Name"
              className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 placeholder-gray-300"/>
          </div>

          {/* Start Date & Time */}
          <div>
            <label className="text-xs font-semibold text-gray-700 block mb-1.5">
              Start Date & Time <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input type="date" value={showUntil} onChange={e=>setShowUntil(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"/>
            </div>
          </div>

          {/* Is it Published */}
          <div className="flex items-center gap-3">
            <Toggle checked={published} onChange={()=>setPublished(v=>!v)}/>
            <span className="text-sm text-gray-700 font-medium">
              Is it Published <span className="text-red-400">*</span>
            </span>
          </div>

          {/* Error */}
          {error && <div className="text-xs text-red-500 font-semibold">{error}</div>}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button onClick={onClose}
              className="py-2.5 rounded-xl border border-brand-300 text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={loading}
              className="py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-50">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Banner card ───────────────────────────────────────────────────────────────
function BannerCard({ banner, onDelete, onEdit, onTogglePublish }: {
  banner: any
  onDelete: () => void
  onEdit: () => void
  onTogglePublish: () => void
}) {
  const imageUrl = banner.pictures?.[0]?.picture?.virtual_path ?? null
  const name     = banner.name ?? '—'
  const showUntil = fmtDate(banner.show_until)
  const isPublished = !!banner.published

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Image */}
      <div className="relative aspect-[16/7] bg-gray-100 overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt={name}
            className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={36} className="text-gray-300"/>
          </div>
        )}

        {/* Action buttons overlay — top right */}
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
          <Can perm={['POST /admin/slider/*', 'POST /admin/sliders/*']}><Toggle checked={isPublished} onChange={onTogglePublish}/></Can>
          <Can perm={['DELETE /admin/slider/*', 'DELETE /admin/sliders/*']}>
          <button onClick={e=>{e.stopPropagation();onDelete()}}
            className="w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center hover:bg-red-50 transition-colors shadow-sm">
            <Trash2 size={13} className="text-red-500"/>
          </button>
          </Can>
          <Can perm={['POST /admin/slider/*', 'POST /admin/sliders/*']}>
          <button onClick={e=>{e.stopPropagation();onEdit()}}
            className="w-7 h-7 rounded-lg bg-white/90 flex items-center justify-center hover:bg-brand-50 transition-colors shadow-sm">
            <Edit2 size={13} className="text-brand-500"/>
          </button>
          </Can>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
        <div className="text-right flex-shrink-0 ml-3">
          <div className="text-xs font-bold text-gray-800">{showUntil}</div>
          <div className="text-[11px] text-gray-400 uppercase font-semibold tracking-wide">Show Until</div>
        </div>
      </div>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 animate-pulse overflow-hidden">
      <div className="aspect-[16/7] bg-gray-200"/>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="h-3 bg-gray-200 rounded w-2/3"/>
        <div className="h-4 bg-gray-100 rounded w-20"/>
      </div>
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage:(p:number)=>void; onPerPage:(n:number)=>void
}) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>Items per page</span>
        <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <span>1 – {Math.min(page*perPage,total)} of {total} items</span>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={()=>onPage(Math.max(1,page-1))} disabled={page<=1}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          <ChevronLeft size={13}/> Previous
        </button>
        <div className="flex items-center gap-1">
          <input type="number" value={page} onChange={e=>onPage(Number(e.target.value))}
            className="w-10 text-center text-xs border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-brand-400"/>
          <span className="text-xs text-gray-500">of {lastPage}</span>
        </div>
        <button onClick={()=>onPage(Math.min(lastPage,page+1))} disabled={page>=lastPage}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          Next <ChevronRight size={13}/>
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function BannerPage() {
  const qc = useQueryClient()
  const { t } = useTranslation()
  const confirm = useConfirm()

  const [search,     setSearch]     = useState('')
  const [statusFilter, setStatus]   = useState('')   // '' | '1' | '0'
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(20)
  const [modalOpen,  setModalOpen]  = useState(false)
  const [editBanner, setEditBanner] = useState<any | null>(null)

  const debouncedSearch = useDebounce(search)

  const { data, isLoading } = useQuery({
    queryKey: ['banners', 'list', { page, perPage, search: debouncedSearch }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: String(perPage) })
      if (debouncedSearch) params.append('search', debouncedSearch)
      return restClient.get(`${endpoints.slider.list}?${params}`).then(r => r.data)
    },
    staleTime: 30_000,
  })

  const allBanners: any[] = (data as any)?.data ?? []
  const banners = statusFilter === ''
    ? allBanners
    : allBanners.filter(b => (statusFilter === '1' ? !!b.published : !b.published))
  const total    = (data as any)?.meta?.total ?? (data as any)?.total ?? allBanners.length
  const lastPage = Math.ceil(total / perPage) || 1

  const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: ['banners'] }), [qc])

  // Delete
  const deleteBanner = useMutation({
    mutationFn: (id: number) => restClient.delete(`${endpoints.slider.delete}${id}`).then(r=>r.data),
    onSuccess: invalidate,
  })

  // Toggle publish
  const togglePublish = useMutation({
    mutationFn: ({ id, published }: { id: number; published: boolean }) =>
      restClient.post(endpoints.slider.update, { slider_id: id, published: published ? 0 : 1 }).then(r=>r.data),
    onSuccess: invalidate,
  })

  const handleEdit = async (banner: any) => {
    // Fetch full detail if needed
    try {
      const res = await restClient.get(`${endpoints.slider.details}${banner.id}`).then(r=>r.data)
      setEditBanner((res as any)?.data ?? banner)
    } catch {
      setEditBanner(banner)
    }
    setModalOpen(true)
  }

  return (
    <div className="space-y-5">

      {/* ── Header toolbar ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-bold text-gray-800">Banner List</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status */}
          <select value={statusFilter} onChange={e=>{setStatus(e.target.value);setPage(1)}}
            className="text-xs border border-gray-200 rounded-lg px-3 py-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-400 min-w-28">
            <option value="">Select Status</option>
            <option value="1">Published</option>
            <option value="0">Unpublished</option>
          </select>
          {/* Date */}
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 min-w-28">
            <Calendar size={12}/><span>Date Filter</span>
          </div>
          {/* Search */}
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-44">
            <Search size={12} className="text-gray-400 flex-shrink-0"/>
            <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Search by name"
              className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"/>
            {search && <button onClick={()=>setSearch('')}><X size={11} className="text-gray-300 hover:text-gray-500"/></button>}
          </div>
          {/* Upload Banner */}
          <Can perm={['POST /admin/slider/*', 'POST /admin/sliders/*']}>
          <button onClick={()=>{ setEditBanner(null); setModalOpen(true) }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-brand-400 text-brand-600 text-xs font-bold hover:bg-brand-50 transition-colors">
            <Plus size={13}/> Upload Banner
          </button>
          </Can>
        </div>
      </div>

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)
          : banners.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <ImageIcon size={24} className="text-gray-300"/>
                </div>
                <p className="text-sm font-semibold text-gray-500">No banners found</p>
              </div>
            )
            : banners.map((b:any)=>(
              <BannerCard
                key={b.id}
                banner={b}
                onDelete={async ()=>{
                  const ok = await confirm({
                    title: t('common.confirmDeleteTitle'),
                    description: t('common.confirmDeleteDescription'),
                    confirmLabel: t('common.delete'),
                  })
                  if (ok) deleteBanner.mutate(b.id)
                }}
                onEdit={()=>handleEdit(b)}
                onTogglePublish={()=>togglePublish.mutate({ id: b.id, published: !!b.published })}
              />
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n=>{setPerPage(n);setPage(1)}}/>
      )}

      {/* ── Upload / Edit Modal ── */}
      <UploadBannerModal
        open={modalOpen}
        onClose={()=>{ setModalOpen(false); setEditBanner(null) }}
        onSuccess={invalidate}
        editBanner={editBanner}
      />
    </div>
  )
}
